import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Truck } from "lucide-react";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";

export interface AtlasFilters {
  locationIds: string[];
  availability: "all" | "available" | "day_off";
  shift: "all" | "AM" | "PM";
  certification: "all" | "VO" | "NVO" | "Manual";
  baseTrucks: string[];
}

export interface AtlasFilterLocation {
  id: string;
  name: string;
  short_code: string | null;
  city: string | null;
}

interface AtlasFilterBarProps {
  filters: AtlasFilters;
  onFiltersChange: (filters: AtlasFilters) => void;
  locations: AtlasFilterLocation[];
}

const PLATFORM_OPTIONS: ChipFilterOption[] = [
  { value: "Peterbilt", label: "Peterbilt" },
  { value: "Volvo", label: "Volvo" },
  { value: "International", label: "International" },
  { value: "Daimler", label: "Daimler" },
];

type FilterKey = "locations" | "platform";

export function AtlasFilterBar({ filters, onFiltersChange, locations }: AtlasFilterBarProps) {
  const [activeFilters, setActiveFilters] = useState<FilterKey[]>(() => {
    const initial: FilterKey[] = [];
    if (filters.locationIds.length > 0) initial.push("locations");
    if (filters.baseTrucks.length > 0) initial.push("platform");
    return initial;
  });
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<FilterKey | null>(null);

  const locationOptions: ChipFilterOption[] = locations.map((l) => {
    const code = l.short_code?.trim();
    const city = l.city?.trim();
    const label = code
      ? `${code}${city ? `: ${city}` : ""}`
      : city ?? l.name ?? "Unknown";
    return { value: l.id, label };
  });

  const addFilter = useCallback((key: string) => {
    const fk = key as FilterKey;
    if (activeFilters.includes(fk)) return;
    setActiveFilters((prev) => [...prev, fk]);
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, [activeFilters]);

  const removeFilter = useCallback((key: FilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
    if (key === "locations") onFiltersChange({ ...filters, locationIds: [] });
    if (key === "platform") onFiltersChange({ ...filters, baseTrucks: [] });
  }, [filters, onFiltersChange]);

  const hasAnyChipFilter = activeFilters.length > 0;

  const resetAllFilters = () => {
    setActiveFilters([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onFiltersChange({
      locationIds: [],
      availability: "all",
      shift: "all",
      certification: "all",
      baseTrucks: [],
    });
  };

  const allCategories: (FilterCategory & { key: FilterKey })[] = [
    { key: "locations", label: "Locations", icon: <MapPin className="h-3.5 w-3.5" /> },
    { key: "platform", label: "Platform", icon: <Truck className="h-3.5 w-3.5" /> },
  ];

  const availableCategories = allCategories
    .filter((c) => !activeFilters.includes(c.key))
    .map(({ key, label, icon }) => ({ key, label, icon }));

  const getFilterValue = (key: FilterKey): string[] => {
    if (key === "locations") return filters.locationIds;
    if (key === "platform") return filters.baseTrucks;
    return [];
  };

  const renderChip = (key: FilterKey) => {
    const categoryIcon = allCategories.find((c) => c.key === key)?.icon;
    const commonProps = {
      icon: categoryIcon,
      open: openFilterKey === key,
      onOpenChange: (isOpen: boolean) => {
        if (!isOpen && ignoreAutoCloseRef.current === key) {
          ignoreAutoCloseRef.current = null;
          return;
        }
        if (!isOpen) {
          const v = getFilterValue(key);
          if (v.length === 0) {
            setActiveFilters((prev) => prev.filter((k) => k !== key));
          }
          setOpenFilterKey((prev) => (prev === key ? null : prev));
        } else {
          setOpenFilterKey(key);
        }
      },
    };

    if (key === "locations") {
      return (
        <ChipFilter
          key="locations"
          label="Locations"
          options={locationOptions}
          value={filters.locationIds}
          onChange={(v) =>
            onFiltersChange({ ...filters, locationIds: Array.isArray(v) ? v : [v] })
          }
          onRemove={() => removeFilter("locations")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "platform") {
      return (
        <ChipFilter
          key="platform"
          label="Platform"
          options={PLATFORM_OPTIONS}
          value={filters.baseTrucks}
          onChange={(v) =>
            onFiltersChange({ ...filters, baseTrucks: Array.isArray(v) ? v : [v] })
          }
          onRemove={() => removeFilter("platform")}
          multiSelect
          {...commonProps}
        />
      );
    }

    return null;
  };

  return (
    <div className="flex items-center gap-1 flex-wrap relative z-[1000]">
      {activeFilters.map((key) => renderChip(key))}

      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={hasAnyChipFilter}
      />

      {hasAnyChipFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={resetAllFilters}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}